import {useState, useRef, useEffect} from "react";
import {useTranslation} from "react-i18next";
import type {TFunction} from "i18next";
import {isToday, subDays} from "date-fns";
import {Text, UnstyledButton, TextInput, ActionIcon} from "@mantine/core";
import {LinkRow} from "../LinkRow/LinkRow";
import IconSearch from "../../assets/search.svg?react";
import classes from "./DashboardLinkList.module.css";

export type LinkListItem = {
    name: string;
    description?: string;
    date?: string;
    sectionLabel?: string;
    rightSection?: React.ReactNode;
} & (
    | {href: string; download?: string; to?: never}
    | {to: string; href?: never; download?: never}
);

export type DashboardLinkListVariant = "flat" | "grouped" | "pinned-grouped";

type DashboardLinkListProps = {
    title: string;
    description?: string;
    items: LinkListItem[];
    variant?: DashboardLinkListVariant;
    maxItems?: number;
    viewAllHref?: string;
    background?: string;
    emptyText?: string;
    loading?: boolean;
    animationIndex?: number;
};

type Group = {
    label: string;
    items: LinkListItem[];
    kind: "section" | "date";
};

function groupByDate(items: LinkListItem[], t: TFunction): Group[] {
    const sevenDaysAgo = subDays(new Date(), 7);
    const today: LinkListItem[] = [];
    const lastWeek: LinkListItem[] = [];
    const previous: LinkListItem[] = [];

    for (const item of items) {
        const date = item.date ? new Date(item.date) : null;
        if (date && isToday(date)) {
            today.push(item);
        } else if (date && date >= sevenDaysAgo) {
            lastWeek.push(item);
        } else {
            previous.push(item);
        }
    }

    const hasRecentGroup = today.length > 0 || lastWeek.length > 0;
    const result: Group[] = [];
    if (today.length > 0)
        result.push({label: t("date.today"), items: today, kind: "date"});
    if (lastWeek.length > 0)
        result.push({
            label: t("date.lastWeek"),
            items: lastWeek,
            kind: "date",
        });
    if (previous.length > 0)
        result.push({
            label: hasRecentGroup ? t("date.previously") : "",
            items: previous,
            kind: "date",
        });
    return result;
}

function groupBySectionLabel(items: LinkListItem[]): Group[] {
    if (!items.some((i) => i.sectionLabel)) {
        return [{label: "", items, kind: "section"}];
    }
    const groups: Group[] = [];
    for (const item of items) {
        const label = item.sectionLabel ?? "";
        const last = groups[groups.length - 1];
        if (last && last.label === label) {
            last.items.push(item);
        } else {
            groups.push({label, items: [item], kind: "section"});
        }
    }
    return groups;
}

/**
 * Pinned items (those carrying a sectionLabel) stay flat at the top; the rest
 * (e.g. last-visited history, which carry a `date`) are date-grouped below —
 * matching the threads column's today / last-week / earlier buckets.
 */
function groupPinnedThenDate(items: LinkListItem[], t: TFunction): Group[] {
    const pinned = items.filter((i) => i.sectionLabel);
    const rest = items.filter((i) => !i.sectionLabel);
    const groups: Group[] = [];
    if (pinned.length > 0) groups.push(...groupBySectionLabel(pinned));
    if (rest.length > 0) groups.push(...groupByDate(rest, t));
    return groups;
}

function filterItems(items: LinkListItem[], query: string): LinkListItem[] {
    if (!query) return items;
    let regex: RegExp;
    try {
        regex = new RegExp(query, "i");
    } catch {
        regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    }
    return items.filter(
        (item) => regex.test(item.name) || (item.description && regex.test(item.description))
    );
}

export function DashboardLinkList({
    title,
    description,
    items,
    variant = "flat",
    maxItems = 5,
    viewAllHref,
    background,
    emptyText,
    loading = false,
    animationIndex = 0,
}: DashboardLinkListProps) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (searchOpen) inputRef.current?.focus();
    }, [searchOpen]);

    // Play the entrance once, on a genuine first load (mounted while loading),
    // then drop the animation classes so later re-renders (search, expand) and
    // tab re-entry don't replay it. `animate` is derived during render so the
    // first content render already carries the class (no fully-visible flash
    // before the animation kicks in).
    const mountedLoading = useRef(loading);
    const [entranceDone, setEntranceDone] = useState(false);
    const animate = mountedLoading.current && !loading && !entranceDone;
    useEffect(() => {
        if (!animate) return;
        const id = setTimeout(() => setEntranceDone(true), 1500);
        return () => clearTimeout(id);
    }, [animate]);

    // Hold the grid slot (so columns don't reflow) while loading; the column
    // falls into place once data arrives.
    if (loading) return <div aria-hidden />;

    if (items.length === 0 && !emptyText) return null;

    const sorted =
        variant === "grouped"
            ? [...items].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
            : items;

    const filtered = filterItems(sorted, query);
    const isSearching = query.length > 0;
    const hasMore = !isSearching && filtered.length > maxItems;
    const visible = isSearching || expanded ? filtered : filtered.slice(0, maxItems);

    const groups: Group[] =
        variant === "grouped"
            ? groupByDate(visible, t)
            : variant === "pinned-grouped"
              ? groupPinnedThenDate(visible, t)
              : groupBySectionLabel(visible);

    const handleCloseSearch = () => {
        setSearchOpen(false);
        setQuery("");
        setExpanded(false);
    };

    // Running index for the row slide-in stagger (top-to-bottom across groups).
    let rowIndex = 0;

    return (
        <div
            className={`${classes.root} ${animate ? classes.columnAnimate : ""}`.trim()}
            style={{
                ...(background ? {backgroundColor: background} : {}),
                ...(animate ? {animationDelay: `${animationIndex * 70}ms`} : {}),
            }}
        >
            <div className={classes.header}>
                <Text className={classes.title}>{title}</Text>
                <div className={classes.searchWrapper}>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        aria-label={t("common.search")}
                        onClick={() => setSearchOpen(true)}
                        className={classes.searchIcon}
                        style={{visibility: searchOpen ? "hidden" : "visible", position: "absolute", right: 0}}
                    >
                        <IconSearch width={16} height={16} />
                    </ActionIcon>
                    <TextInput
                        ref={inputRef}
                        size="xs"
                        placeholder={`${t("common.search")}…`}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.currentTarget.value);
                            setExpanded(false);
                        }}
                        onBlur={() => {
                            if (!query) handleCloseSearch();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") handleCloseSearch();
                        }}
                        style={{visibility: searchOpen ? "visible" : "hidden"}}
                        classNames={{root: classes.searchRoot, input: classes.searchInput}}
                    />
                </div>
            </div>
            {description && (
                <Text className={classes.description}>{description}</Text>
            )}
            {items.length === 0 && emptyText && (
                <Text className={classes.emptyText}>{emptyText}</Text>
            )}
            {groups.map((group) => (
                <div key={group.label}>
                    {group.label && (
                        <Text className={classes.sectionLabel}>
                            {group.label}
                        </Text>
                    )}
                    {group.items.map((item, i) => (
                        <div
                            key={i}
                            className={animate ? classes.rowAnimate : undefined}
                            style={animate ? {animationDelay: `${Math.min(rowIndex++, 12) * 35}ms`} : undefined}
                        >
                            {item.to ? (
                                <LinkRow
                                    to={item.to}
                                    name={item.name}
                                    description={item.description}
                                    rightSection={item.rightSection}
                                />
                            ) : (
                                <LinkRow
                                    href={item.href ?? ""}
                                    download={item.download}
                                    name={item.name}
                                    description={item.description}
                                    rightSection={item.rightSection}
                                />
                            )}
                        </div>
                    ))}
                </div>
            ))}
            {isSearching && filtered.length === 0 && (
                <Text className={classes.emptyText}>
                    {t("Phrase.NoSearchResults")}
                </Text>
            )}
            {hasMore && (
                <UnstyledButton className={classes.expandButton} onClick={() => setExpanded((e) => !e)}>
                    {expanded
                        ? t("common.showLess")
                        : t("common.showAll", {
                              count: filtered.length,
                              defaultValue: "Show all ({{count}})",
                          })}
                </UnstyledButton>
            )}
        </div>
    );
}
